import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { app } from "@microsoft/teams-js";

export type AppThemeName = "light" | "dark" | "contrast";

type ThemeSource = "browser" | "teams";
type ThemeStatus = "loading" | "ready" | "fallback";
const THEME_STORAGE_KEY = "utech-app-theme";

function normalizeTheme(theme?: string | null): AppThemeName {
  switch (theme?.toLowerCase()) {
    case "dark":
      return "dark";
    case "contrast":
    case "highcontrast":
    case "high-contrast":
      return "contrast";
    default:
      return "light";
  }
}

function resolveBrowserTheme(): AppThemeName {
  return "light";
}

function resolveInitialTheme(): AppThemeName {
  if (typeof document !== "undefined") {
    const bootTheme = normalizeTheme(document.documentElement.dataset.appTheme);
    if (bootTheme) {
      return bootTheme;
    }
  }

  return resolveBrowserTheme();
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Timed out while initializing Teams host context."));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

export function useHostTheme() {
  const [themeName, setThemeName] = useState<AppThemeName>(() => resolveInitialTheme());
  const [themeSource, setThemeSource] = useState<ThemeSource>("browser");
  const [themeStatus, setThemeStatus] = useState<ThemeStatus>("loading");

  useEffect(() => {
    document.documentElement.dataset.appTheme = themeName;
    document.documentElement.dataset.themeStatus = themeStatus;

    try {
      window.sessionStorage.setItem(THEME_STORAGE_KEY, themeName);
    } catch {}
  }, [themeName, themeStatus]);

  // O disposed vive num ref, e não numa variável local do efeito, porque o registro do handler
  // também é chamado de fora dele: a suspensão do Teams apaga os handlers registrados no host, e
  // quem rearma é o caminho de resume (useTeamsAppSuspension), muito depois do mount.
  const disposedRef = useRef(false);

  const registerThemeChangeHandler = useCallback(() => {
    app.registerOnThemeChangeHandler((nextTheme) => {
      if (!disposedRef.current) {
        setThemeName(normalizeTheme(nextTheme));
      }
    });
  }, []);

  /**
   * Relê o tema do host sob demanda.
   *
   * Existe porque o Teams web **não entrega** `themeChange` para a aplicação: verificado com o
   * handler comprovadamente registrado (`themeStatus: "ready"`) e um listener de `message` no
   * frame da aplicação, que não recebeu nada ao trocar o tema. Sem isto, o tema fica congelado no
   * valor lido no boot pelo resto da vida do documento — e a suspensão estende essa vida
   * indefinidamente. No desktop o handler deve funcionar normalmente; aqui é redundância barata.
   *
   * Escreve **apenas** `themeName`, e isso é o que a torna segura de chamar em qualquer momento:
   * re-renderiza o App e regenera o tema do MUI, sem trocar a branch renderizada nem a URL do
   * iframe do softphone. `locale` jamais pode ser relido por este caminho — ele alimenta a query
   * do iframe, e mexer nele recarregaria o softphone, derrubando a chamada em curso.
   */
  const refreshHostTheme = useCallback(async () => {
    try {
      await withTimeout(app.initialize(), 1500);
      const context = await withTimeout(app.getContext(), 1500);
      if (disposedRef.current) return;

      const nextTheme = normalizeTheme(context.app.theme);
      setThemeName((current) => {
        if (current !== nextTheme) {
          console.log(`[theme] host=${nextTheme} (atualizado de ${current})`);
        }
        return nextTheme;
      });
    } catch {
      // Fora do Teams ou host lento: mantém o tema atual, que já é o melhor palpite disponível.
    }
  }, []);

  useEffect(() => {
    disposedRef.current = false;

    async function initializeTeamsTheme() {
      try {
        await withTimeout(app.initialize(), 1500);
        const context = await withTimeout(app.getContext(), 1500);

        if (disposedRef.current) return;

        setThemeSource("teams");
        setThemeName(normalizeTheme(context.app.theme));
        setThemeStatus("ready");

        registerThemeChangeHandler();
      } catch {
        if (!disposedRef.current) {
          setThemeSource("browser");
          setThemeName(resolveBrowserTheme());
          setThemeStatus("fallback");
        }
      }
    }

    void initializeTeamsTheme();

    return () => {
      disposedRef.current = true;
    };
  }, [registerThemeChangeHandler]);

  return useMemo(
    () => ({
      isInTeams: themeSource === "teams",
      refreshHostTheme,
      registerThemeChangeHandler,
      themeName,
      themeStatus,
      themeSource,
    }),
    [refreshHostTheme, registerThemeChangeHandler, themeName, themeSource, themeStatus]
  );
}